/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Autosuggestions } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductRefinerValue, SearchSuggestion, SearchSuggestionCriteria } from '@msdyn365-commerce/retail-proxy';
import { getSearchSuggestionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { generateImageUrl, getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';

export enum SuggestionType {
    Category = 'ScopedCategory',
    Keyword = 'Keyword',
    Product = 'Product',
    None = 'None'
}

/**
 * Input class for auto suggest search input.
 */
export class AutoSuggestInput implements IActionInput {
    public searchQuery?: string;

    public topResultsCount?: number;

    public suggestionType?: string;

    public hitPrefix?: string;

    public hitSuffix?: string;

    constructor(searchText?: string, top?: number, suggestionType?: string, hitPrefix?: string, hitSuffix?: string) {
        this.searchQuery = searchText;
        this.topResultsCount = top;
        this.suggestionType = suggestionType;
        this.hitPrefix = hitPrefix;
        this.hitSuffix = hitSuffix;
    }

    public getCacheKey = () => 'AutoSuggestSearchSuggestions';

    public getCacheObjectType = () => 'AutoSuggestSearchSuggestions';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new AutoSuggestInput();
};

/**
 * Calls the Retail API and returns a auto-suggest suggestions.
 * @param input
 * @param ctx
 */
export async function getSearchSuggestionsAction(input: AutoSuggestInput, ctx: IActionContext): Promise<Autosuggestions | null> {
    // If no input is provided fail out
    if (!input || !input.searchQuery) {
        throw new Error('[getSearchSuggestionsAction]No valid Input was provided, failing');
    }

    const autosuggest: Autosuggestions = {};

    const autoSuggestPromises = [_getAutoSuggest(input, autosuggest, ctx)];
    return Promise.all(autoSuggestPromises).then(() => {
        return autosuggest;
    });
}

async function _getAutoSuggest(input: AutoSuggestInput, autosuggest: Autosuggestions, ctx: IActionContext): Promise<void> {
    const searchCriteria: SearchSuggestionCriteria = {
        ProductSearchCriteria: {
            SearchCondition: input.searchQuery && input.searchQuery.length > 0 ? input.searchQuery : '',
            Context: {
                ChannelId: +ctx.requestContext.apiSettings.channelId,
                CatalogId: +ctx.requestContext.apiSettings.catalogId
            },
            Refinement: []
        },
        HitPrefix: input.hitPrefix,
        HitSuffix: input.hitSuffix,
        SuggestionType: input.suggestionType
    };

    const USE_VIEW_RESTRICTIONS = process.env.USE_VIEW_RESTRICTIONS;
    if (parseInt(USE_VIEW_RESTRICTIONS || '', 10) === 1) {
        const RECOMMEND_REFINER_RECORD_ID = process.env.RECOMMEND_REFINER_RECORD_ID;
        const ENDOWMENT_REFINER_RECORD_ID = process.env.ENDOWMENT_REFINER_RECORD_ID;
        const TEMPLE_REFINER_RECORD_ID = process.env.TEMPLE_REFINER_RECORD_ID;

        const recommendAttribute: ProductRefinerValue = {
            RefinerRecordId: parseInt(RECOMMEND_REFINER_RECORD_ID || '', 10),
            RightValueBoundString: 'Recommend_NO',
            LeftValueBoundString: 'Recommend_NO',
            RefinerSourceValue: 1,
            Count: 4,
            DataTypeValue: 5,
            ExtensionProperties: [],
            LeftValueBoundLocalizedString: '',
            RightValueBoundLocalizedString: '',
            RowNumber: 0,
            SwatchColorHexCode: '',
            SwatchImageUrl: '',
            UnitText: '',
            //@ts-ignore
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.ProductRefinerValue'
        };
        const endowmentAttribute: ProductRefinerValue = {
            RefinerRecordId: parseInt(ENDOWMENT_REFINER_RECORD_ID || '', 10),
            RightValueBoundString: 'Endowment_NO',
            LeftValueBoundString: 'Endowment_NO',
            RefinerSourceValue: 1,
            Count: 4,
            DataTypeValue: 5,
            ExtensionProperties: [],
            LeftValueBoundLocalizedString: '',
            RightValueBoundLocalizedString: '',
            RowNumber: 0,
            SwatchColorHexCode: '',
            SwatchImageUrl: '',
            UnitText: '',
            //@ts-ignore
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.ProductRefinerValue'
        };
        const templeAttribute: ProductRefinerValue = {
            RefinerRecordId: parseInt(TEMPLE_REFINER_RECORD_ID || '', 10),
            RightValueBoundString: 'Temple_NO',
            LeftValueBoundString: 'Temple_NO',
            RefinerSourceValue: 1,
            Count: 4,
            DataTypeValue: 5,
            ExtensionProperties: [],
            LeftValueBoundLocalizedString: '',
            RightValueBoundLocalizedString: '',
            RowNumber: 0,
            SwatchColorHexCode: '',
            SwatchImageUrl: '',
            UnitText: '',
            //@ts-ignore
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.ProductRefinerValue'
        };

        const isUserAuthenticated = ctx.requestContext.user.isAuthenticated;
        if (isUserAuthenticated) {
            const customerInput = new GetCustomerInput(ctx.requestContext.apiSettings, ctx.requestContext.user.customerAccountNumber);
            const customer = await getCustomer(customerInput, ctx);

            if (!customer?.ExtensionProperties?.find(x => x.Key === 'CMISRecommend')?.Value?.BooleanValue) {
                if (
                    !searchCriteria?.ProductSearchCriteria?.Refinement?.find(
                        refiner => refiner.RefinerRecordId === recommendAttribute.RefinerRecordId
                    )
                ) {
                    searchCriteria?.ProductSearchCriteria?.Refinement?.push(recommendAttribute);
                }

                if (!customer?.ExtensionProperties?.find(x => x.Key === 'CMISEndowment')?.Value?.BooleanValue) {
                    if (
                        !searchCriteria?.ProductSearchCriteria?.Refinement?.find(
                            refiner => refiner.RefinerRecordId === endowmentAttribute.RefinerRecordId
                        )
                    ) {
                        searchCriteria?.ProductSearchCriteria?.Refinement?.push(endowmentAttribute);
                    }
                }
            }
            if (!customer?.ExtensionProperties?.find(x => x.Key === 'CMISTemple')?.Value?.BooleanValue) {
                if (
                    !searchCriteria?.ProductSearchCriteria?.Refinement?.find(
                        refiner => refiner.RefinerRecordId === templeAttribute.RefinerRecordId
                    )
                ) {
                    searchCriteria?.ProductSearchCriteria?.Refinement?.push(templeAttribute);
                }
            }
        } else {
            if (
                !searchCriteria?.ProductSearchCriteria?.Refinement?.find(
                    refiner => refiner.RefinerRecordId === endowmentAttribute.RefinerRecordId
                )
            ) {
                searchCriteria?.ProductSearchCriteria?.Refinement?.push(endowmentAttribute);
            }
            if (
                !searchCriteria?.ProductSearchCriteria?.Refinement?.find(
                    refiner => refiner.RefinerRecordId === recommendAttribute.RefinerRecordId
                )
            ) {
                searchCriteria?.ProductSearchCriteria?.Refinement?.push(recommendAttribute);
            }
            if (
                !searchCriteria?.ProductSearchCriteria?.Refinement?.find(
                    refiner => refiner.RefinerRecordId === templeAttribute.RefinerRecordId
                )
            ) {
                searchCriteria?.ProductSearchCriteria?.Refinement?.push(templeAttribute);
            }
        }
    }

    const searchSuggestions = await getSearchSuggestionsAsync(
        { callerContext: ctx, queryResultSettings: { Paging: { Top: input.topResultsCount } } },
        searchCriteria
    );

    if (searchSuggestions && searchSuggestions.length > 0) {
        // Generate image url
        searchSuggestions.map(
            (item: SearchSuggestion) =>
                (item.ImageUrl = item.ImageUrl ? generateImageUrl(item.ImageUrl, ctx.requestContext.apiSettings) : '')
        );

        autosuggest.AllSearchResults = searchSuggestions;
    } else {
        ctx.telemetry.error(
            `[getSearchSuggestionsAction] unable to get availabilites for search with suggestion type ${searchCriteria.SuggestionType}`
        );
    }
}

export const getSearchSuggestionsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/search/get-auto-suggest-suggestions',
    action: <IAction<Autosuggestions>>getSearchSuggestionsAction,
    input: createInput
});

export default getSearchSuggestionsActionDataAction;
